import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import StatsGrid from '@/components/StatsGrid';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import api from '@/lib/api';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import {
  Plus, Send, Mail, Users, Calendar,
  Edit, Trash2, Eye, CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';


const Campaigns = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  const [campaigns, setCampaigns] = useState([]);
  const [events, setEvents] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewCampaign, setPreviewCampaign] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  
  const [campaignData, setCampaignData] = useState({
    name: '',
    subject: '',
    message: '',
    event_id: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [campaignsRes, eventsRes, submissionsRes] = await Promise.all([
        api.get(`/campaigns`),
        api.get(`/events`),
        api.get(`/form-submissions`)
      ]);

      setCampaigns(campaignsRes.data);
      setEvents(eventsRes.data);

      // Extract unique emails from submissions
      const emails = submissionsRes.data
        .map(s => s.data?.email)
        .filter(email => email && email.includes('@'));
      setSubscribers([...new Set(emails)]);
    } catch (error) {
      console.error('Failed to fetch data', error);
      toast.error('Failed to load data');
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (!['admin', 'senior_partner'].includes(user.role)) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
        return;
      }
      fetchData();
    }
  }, [user, loading, navigate, fetchData]);

  const saveCampaign = async () => {
    try {
      const campaign = {
        id: editingCampaign?.id || uuidv4(),
        ...campaignData,
        event_id: campaignData.event_id || null,
        sent_to: editingCampaign?.sent_to || [],
        status: editingCampaign?.status || 'draft',
        created_at: editingCampaign?.created_at || new Date().toISOString(),
        sent_at: editingCampaign?.sent_at || null
      };

      if (editingCampaign) {
        await api.put(`/campaigns/${campaign.id}`, campaign);
        toast.success('Campaign updated successfully');
      } else {
        await api.post(`/campaigns`, campaign);
        toast.success('Campaign created successfully');
      }

      setShowCreateDialog(false);
      setEditingCampaign(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save campaign', error);
      toast.error('Failed to save campaign');
    }
  };

  const deleteCampaign = async (id) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success('Campaign deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const sendCampaign = async (campaignId) => {
    if (subscribers.length === 0) {
      toast.error('No subscribers to send to. Form submissions with emails are required.');
      return;
    }
    
    if (!confirm(`Are you sure you want to send this campaign to ${subscribers.length} subscribers?`)) return;

    try {
      const response = await api.post(
        `/campaigns/${campaignId}/send`,
        {}
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Failed to send campaign', error);
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    }
  };

  const resetForm = () => {
    setCampaignData({ name: '', subject: '', message: '', event_id: '' });
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignData({
      name: campaign.name,
      subject: campaign.subject,
      message: campaign.message,
      event_id: campaign.event_id || ''
    });
    setShowCreateDialog(true);
  };

  const openPreview = (campaign) => {
    setPreviewCampaign(campaign);
    setShowPreviewDialog(true);
  };

  const getEventTitle = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event ? event.title : null;
  };

  const applyEventTemplate = (event) => {
    if (!event) return;
    setCampaignData({
      ...campaignData,
      name: `${event.title} - Invitation`,
      subject: `You're Invited: ${event.title}`,
      message: `
<p>Dear Client,</p>

<p>We are pleased to invite you to our upcoming event:</p>

<h3>${event.title}</h3>
<p><strong>Date:</strong> ${new Date(event.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
<p><strong>Time:</strong> ${new Date(event.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p>
${event.location ? `<p><strong>Location:</strong> ${event.location}</p>` : ''}

<p>${event.description || ''}</p>

<p>We hope to see you there!</p>

<p>Best regards,<br>
Forty Chambers</p>
      `.trim(),
      event_id: event.id
    });
  };

  const stats = {
    total: campaigns.length,
    draft: campaigns.filter(c => c.status === 'draft').length,
    sent: campaigns.filter(c => c.status === 'sent').length,
    totalRecipients: campaigns.reduce((sum, c) => sum + (c.sent_to?.length || 0), 0)
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title="Email Campaigns"
        titleTestId="campaigns-title"
        actions={
          <Button
            onClick={() => {
              setEditingCampaign(null);
              resetForm();
              setShowCreateDialog(true);
            }}
            data-testid="create-campaign-btn"
          >
            <Plus size={20} /> Create Campaign
          </Button>
        }
      />

      {/* Stats Cards */}
      <StatsGrid cols={4}>
        <StatCard icon={Mail} iconClassName="text-primary" value={stats.total} label="Total Campaigns" />
        <StatCard icon={Clock} iconClassName="text-yellow-500" valueClassName="text-yellow-600" value={stats.draft} label="Drafts" />
        <StatCard icon={CheckCircle} iconClassName="text-green-500" valueClassName="text-green-600" value={stats.sent} label="Sent" />
        <StatCard icon={Users} valueClassName="text-accent" value={subscribers.length} label="Subscribers" />
      </StatsGrid>

      {/* Info Card */}
      <Card className="mb-8">
          <CardContent className="py-4 px-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-accent shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-sans text-sm text-muted-foreground">
                  Email campaigns are sent to all clients who have submitted forms with valid email addresses. 
                  Currently <strong>{subscribers.length}</strong> subscribers available.
                  {subscribers.length === 0 && (
                    <span className="text-orange-600"> Submit a form with an email to add subscribers.</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Campaigns List */}
        <Tabs defaultValue="all" className="space-y-6">
          <TabsList>
            <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="draft">Drafts ({stats.draft})</TabsTrigger>
            <TabsTrigger value="sent">Sent ({stats.sent})</TabsTrigger>
          </TabsList>

          {['all', 'draft', 'sent'].map(tab => (
            <TabsContent key={tab} value={tab}>
              {campaigns.filter(c => tab === 'all' || c.status === tab).length > 0 ? (
                <div className="space-y-4">
                  {campaigns
                    .filter(c => tab === 'all' || c.status === tab)
                    .map((campaign) => (
                      <Card key={campaign.id} data-testid={`campaign-card-${campaign.id}`}>
                        <CardContent className="p-6">
                          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-serif text-xl font-semibold text-primary">
                                  {campaign.name}
                                </h3>
                                <Badge className={campaign.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                                  {campaign.status}
                                </Badge>
                                {campaign.event_id && (
                                  <Badge variant="outline" className="flex items-center gap-1">
                                    <Calendar size={12} /> {getEventTitle(campaign.event_id) || 'Event'}
                                  </Badge>
                                )}
                              </div>
                              <p className="font-sans text-sm font-medium text-primary mb-1">
                                Subject: {campaign.subject}
                              </p>
                              <p className="font-sans text-sm text-muted-foreground line-clamp-2 mb-2">
                                {campaign.message.replace(/<[^>]*>/g, '').substring(0, 150)}...
                              </p>
                              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                                <span>Created: {new Date(campaign.created_at).toLocaleDateString()}</span>
                                {campaign.status === 'sent' && (
                                  <>
                                    <span>Sent: {new Date(campaign.sent_at).toLocaleDateString()}</span>
                                    <span className="text-green-600 font-medium">
                                      {campaign.sent_to?.length || 0} recipients
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openPreview(campaign)}
                                data-testid={`preview-campaign-${campaign.id}`}
                              >
                                <Eye size={16} className="mr-1" /> Preview
                              </Button>
                              {campaign.status === 'draft' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditDialog(campaign)}
                                  >
                                    <Edit size={16} className="mr-1" /> Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-accent text-accent-foreground hover:bg-accent/90"
                                    onClick={() => sendCampaign(campaign.id)}
                                    disabled={subscribers.length === 0}
                                    data-testid={`send-campaign-${campaign.id}`}
                                  >
                                    <Send size={16} className="mr-1" /> Send
                                  </Button>
                                </>
                              )}
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteCampaign(campaign.id)}
                                aria-label={`Delete campaign ${campaign.name}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              ) : (
                <EmptyState
                  icon={Mail}
                  message={tab === 'all' ? 'No campaigns created yet' : `No ${tab} campaigns`}
                >
                  {tab !== 'sent' && (
                    <Button
                      className="mt-4"
                      onClick={() => {
                        setEditingCampaign(null);
                        resetForm();
                        setShowCreateDialog(true);
                      }}
                    >
                      Create Your First Campaign
                    </Button>
                  )}
                </EmptyState>
              )}
            </TabsContent>
          ))}
        </Tabs>

      {/* Create/Edit Campaign Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              {editingCampaign ? 'Edit Campaign' : 'Create Email Campaign'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Event Linking */}
            {events.length > 0 && !editingCampaign && (
              <div className="p-4 bg-accent/10 rounded-lg">
                <Label className="text-accent font-medium">Quick Start: Link to Event</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Select an event to auto-fill invitation details
                </p>
                <Select
                  value={campaignData.event_id}
                  onValueChange={(value) => {
                    const event = events.find(e => e.id === value);
                    if (event) applyEventTemplate(event);
                  }}
                >
                  <SelectTrigger data-testid="event-select">
                    <SelectValue placeholder="Select an event (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.title} - {new Date(event.date).toLocaleDateString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Campaign Name (Internal) *</Label>
              <Input
                value={campaignData.name}
                onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                placeholder="e.g., March Event Invitation"
                data-testid="campaign-name-input"
              />
            </div>
            
            <div>
              <Label>Email Subject *</Label>
              <Input
                value={campaignData.subject}
                onChange={(e) => setCampaignData({ ...campaignData, subject: e.target.value })}
                placeholder="Subject line recipients will see"
                data-testid="campaign-subject-input"
              />
            </div>
            
            <div>
              <Label>Email Message (HTML supported) *</Label>
              <Textarea
                value={campaignData.message}
                onChange={(e) => setCampaignData({ ...campaignData, message: e.target.value })}
                placeholder="Write your email content here. HTML tags like <p>, <h3>, <strong> are supported."
                rows={12}
                className="font-mono text-sm"
                data-testid="campaign-message-input"
              />
            </div>

            <div className="p-3 bg-secondary rounded-lg text-sm text-muted-foreground">
              <p className="font-medium text-primary mb-1">Preview Info:</p>
              <p>This campaign will be sent to <strong>{subscribers.length}</strong> subscribers when you click Send.</p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                onClick={() => {
                  setShowCreateDialog(false);
                  setEditingCampaign(null);
                  resetForm();
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveCampaign}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={!campaignData.name || !campaignData.subject || !campaignData.message}
                data-testid="save-campaign-btn"
              >
                {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-bold text-primary">
              Email Preview
            </DialogTitle>
          </DialogHeader>
          {previewCampaign && (
            <div className="mt-4">
              <div className="bg-secondary p-4 rounded-lg mb-4">
                <p className="text-sm"><strong>From:</strong> Forty Chambers</p>
                <p className="text-sm"><strong>Subject:</strong> {previewCampaign.subject}</p>
                <p className="text-sm"><strong>To:</strong> {subscribers.length} subscribers</p>
              </div>
              <div className="border border-border rounded-lg p-6">
                <div className="prose prose-sm max-w-none">
                  {/* Subject rendered as a text node (never HTML). Message may be
                      HTML (email body), so sanitize it before injecting. */}
                  <h2 style={{ color: '#0F172A', marginTop: 0 }} data-testid="preview-subject">
                    {previewCampaign.subject}
                  </h2>
                  <div
                    style={{ margin: '20px 0' }}
                    data-testid="preview-message"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(previewCampaign.message || '')
                    }}
                  />
                  <hr style={{ border: '1px solid #E2E8F0', margin: '20px 0' }} />
                  <p style={{ fontSize: '14px', color: '#64748B' }}>
                    Forty Chambers - Professional Legal Services
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default Campaigns;
